import { infiniteQueryOptions, queryOptions, type UseQueryResult } from "@tanstack/react-query";
import { ApiError } from "@repo/api-client";
import type { Entry, Me } from "@repo/shared-types";
import { api } from "./api";
import type { BoardFilters } from "./feedback";
import { queryClient, startAfresh } from "./query";

/**
 * One query per api read (0075), shared by every page that shows it: a
 * loader warms them through `read` (`lib/query.ts`), and the page reads the
 * same ones with `useSuspenseQuery`. A key is the read's path, so a space's
 * reads all sit under `["t", slug]`.
 *
 * A list paged by address — a space's feed, the log — holds each page as a
 * query of its own. One paged by "Show more" is either a first page with the
 * rest read by fetcher and kept where they are shown (Home, a thread), or an
 * infinite query holding every page read (a space's members, requests and
 * invitations, 0084).
 */

/** Board options: a level (none when the part has none), a division and the page. */
export type BoardQuery = { level?: string | null; division: string; cursor?: string | null; limit?: number };

/** A queue's or the hidden list's page, and its size; the api's own when left out. */
export type PageQuery = { cursor?: string | null; limit?: number };

/** A read's catch where a 404 is an answer — nothing there for this viewer — rather than a failure. */
export function noneWhenMissing(error: unknown): null {
  if (error instanceof ApiError && error.status === 404) return null;
  throw error;
}

/**
 * What a `useQuery` holds where a 404 is an answer: none. `useQuery` does not
 * throw it, and keeps the last answer beside a later failure, so the failure
 * is the news.
 */
export function heldBy<T>(query: UseQueryResult<T>): T | null {
  return query.error instanceof ApiError && query.error.status === 404 ? null : (query.data ?? null);
}

let fetchMe: () => Promise<Me> = () => api.me.get();

/** Injection seam for tests; the app never passes this. */
export function __setMeFetcher(fetcher: (() => Promise<Me>) | null) {
  fetchMe = fetcher ?? (() => api.me.get());
}

export const queries = {
  /**
   * Who is signed in, or `null`: 401 is the ordinary answer for a visitor.
   * For a copy that says somebody is, it is news — the session ended in
   * another tab, or ran out — so it fails like any read answering 401, and
   * the cache is emptied and the visitor sent to sign-in (`lib/query.ts`).
   * Somebody else signed in — in another tab, on a shared computer — is the
   * same news: nothing read as one person is drawn for another (0075).
   */
  me: () =>
    queryOptions({
      queryKey: ["me"],
      queryFn: async (): Promise<Me | null> => {
        try {
          const me = await fetchMe();
          const held = queryClient.getQueryData<Me | null>(["me"]);
          if (held && held.user.id !== me.user.id) startAfresh();
          return me;
        } catch (error) {
          if (error instanceof ApiError && error.status === 401 && !queryClient.getQueryData(["me"])) return null;
          throw error;
        }
      },
    }),
  profile: () => queryOptions({ queryKey: ["me", "profile"], queryFn: () => api.me.profile.get() }),
  accountDeletion: () => queryOptions({ queryKey: ["me", "deletion"], queryFn: () => api.me.deletion() }),
  providers: () => queryOptions({ queryKey: ["auth", "providers"], queryFn: () => api.auth.providers() }),

  feed: (view: "all" | "following") =>
    queryOptions({ queryKey: ["feed", view], queryFn: () => api.feed(null, view) }),
  inbox: (cursor: string | null = null) =>
    queryOptions({ queryKey: ["inbox", { cursor }], queryFn: () => api.inbox.list(cursor) }),
  waiting: () => queryOptions({ queryKey: ["inbox", "waiting"], queryFn: () => api.inbox.waiting() }),
  invitations: () => queryOptions({ queryKey: ["invitations"], queryFn: () => api.invitations.list() }),
  sentRequests: () => queryOptions({ queryKey: ["requests"], queryFn: () => api.requests.list() }),
  /**
   * Searching for spaces, a page of 20 at a time with "Show more" (0085). The
   * api matches whatever the case, so "Ana" and "ana" share one copy.
   */
  searchSpaces: (search: string) => {
    const q = search.trim().toLowerCase();
    return infiniteQueryOptions({
      queryKey: ["tenants", "search", { q }],
      queryFn: ({ pageParam }) => api.tenants.search(q, pageParam),
      initialPageParam: null as string | null,
      getNextPageParam: (page) => page.nextCursor,
      // One per term typed: let a term not searched for a while go.
      gcTime: 5 * 60_000,
    });
  },
  /**
   * The feedback board (0091), 30 at a time with "Show more", under the
   * filters in the address. Newest first, so a page read again after a write
   * is stable enough to hold every page read so far.
   */
  feedback: (filters: BoardFilters) =>
    infiniteQueryOptions({
      queryKey: ["feedback", { kind: filters.kind, status: filters.status }],
      // A group with every chip off matches nothing; the api would read the
      // filter left out as every one, so it is not asked.
      queryFn: ({ pageParam }) =>
        filters.kind.length === 0 || filters.status.length === 0
          ? Promise.resolve({ feedback: [], nextCursor: null })
          : api.feedback.list(filters, pageParam),
      initialPageParam: null as string | null,
      getNextPageParam: (page) => page.nextCursor,
    }),
  feedbackItem: (id: string) => queryOptions({ queryKey: ["feedback", id], queryFn: () => api.feedback.get(id) }),
  preview: (slug: string) =>
    queryOptions({ queryKey: ["tenants", slug, "preview"], queryFn: () => api.tenants.preview(slug) }),
  eventOptions: () => queryOptions({ queryKey: ["event-options"], queryFn: () => api.eventOptions() }),

  /** The log, under /t/me. */
  entries: (cursor: string | null = null) =>
    queryOptions({ queryKey: ["t", "me", "entries", { cursor }], queryFn: () => api.entries.list(cursor) }),
  entry: (id: string) => queryOptions({ queryKey: ["t", "me", "entries", id], queryFn: () => api.entries.get(id) }),
  entryComments: (id: string) =>
    queryOptions({ queryKey: ["t", "me", "entries", id, "comments"], queryFn: () => api.entries.comments(id) }),
  teamComments: (teamId: string) =>
    queryOptions({ queryKey: ["t", "me", "teams", teamId, "comments"], queryFn: () => api.entries.teams.comments(teamId) }),

  /** One space's reads, each under `["t", slug]`. */
  space: (slug: string) => queryOptions({ queryKey: ["t", slug], queryFn: () => api.space(slug).get() }),
  /**
   * The lists that grow with people are infinite queries: a page of 50, and
   * "Show more" reads the next. Every page read is read again after a change,
   * in turn, by the query itself. Members' `q` is its search.
   */
  members: (slug: string, search = "") => {
    // The api matches whatever the case, so "Ana" and "ana" share one copy.
    const q = search.trim().toLowerCase();
    return infiniteQueryOptions({
      queryKey: ["t", slug, "members", { q }],
      queryFn: ({ pageParam }) => api.space(slug).members.list({ cursor: pageParam, q }),
      initialPageParam: null as string | null,
      getNextPageParam: (page) => page.nextCursor,
    });
  },
  spaceInvitations: (slug: string) =>
    infiniteQueryOptions({
      queryKey: ["t", slug, "invitations"],
      queryFn: ({ pageParam }) => api.space(slug).invitations.list(pageParam),
      initialPageParam: null as string | null,
      getNextPageParam: (page) => page.nextCursor,
    }),
  spaceRequests: (slug: string) =>
    infiniteQueryOptions({
      queryKey: ["t", slug, "requests"],
      queryFn: ({ pageParam }) => api.space(slug).requests.list(pageParam),
      initialPageParam: null as string | null,
      getNextPageParam: (page) => page.nextCursor,
    }),
  spaceDeletion: (slug: string) =>
    queryOptions({ queryKey: ["t", slug, "deletion"], queryFn: () => api.space(slug).deletion() }),
  leaving: (slug: string) =>
    queryOptions({ queryKey: ["t", slug, "leaving"], queryFn: () => api.space(slug).leaving() }),
  events: (slug: string, cursor: string | null = null) =>
    queryOptions({ queryKey: ["t", slug, "events", { cursor }], queryFn: () => api.space(slug).events.list(cursor) }),

  /** One event's reads, each under `["t", slug, "events", id]`. */
  event: (slug: string, id: string) =>
    queryOptions({ queryKey: ["t", slug, "events", id], queryFn: () => api.space(slug).events.get(id) }),
  eventComments: (slug: string, id: string) =>
    queryOptions({
      queryKey: ["t", slug, "events", id, "comments"],
      queryFn: () => api.space(slug).events.conversation(id).comments(),
    }),
  /** The caller's result for an event, or `null`: 404 is the ordinary answer for none. */
  myEntry: (slug: string, eventId: string) =>
    queryOptions({
      queryKey: ["t", slug, "events", eventId, "entry"],
      queryFn: async (): Promise<Entry | null> => {
        return api.space(slug).events.entry.get(eventId).catch(noneWhenMissing);
      },
    }),
  result: (slug: string, eventId: string, entryId: string) =>
    queryOptions({
      queryKey: ["t", slug, "events", eventId, "shares", entryId],
      queryFn: () => api.space(slug).events.results.get(eventId, entryId),
    }),
  resultComments: (slug: string, eventId: string, entryId: string) =>
    queryOptions({
      queryKey: ["t", slug, "events", eventId, "shares", entryId, "comments"],
      queryFn: () => api.space(slug).events.results.conversation(eventId, entryId).comments(),
    }),
  leaderboard: (slug: string, eventId: string, partId: string, board: BoardQuery) =>
    queryOptions({
      queryKey: ["t", slug, "events", eventId, "parts", partId, "leaderboard", board],
      queryFn: () => api.space(slug).events.leaderboard(eventId, partId, board),
    }),
  overall: (slug: string, eventId: string, board: BoardQuery) =>
    queryOptions({
      queryKey: ["t", slug, "events", eventId, "overall", board],
      queryFn: () => api.space(slug).events.overall(eventId, board),
    }),
  queue: (slug: string, eventId: string, page: PageQuery = {}) =>
    queryOptions({
      queryKey: ["t", slug, "events", eventId, "queue", page],
      queryFn: () => api.space(slug).events.queue(eventId, page),
    }),
  hidden: (slug: string, eventId: string, page: PageQuery = {}) =>
    queryOptions({
      queryKey: ["t", slug, "events", eventId, "hidden", page],
      queryFn: () => api.space(slug).events.hidden(eventId, page),
    }),
  myTeams: (slug: string, eventId: string) =>
    queryOptions({
      queryKey: ["t", slug, "events", eventId, "teams"],
      queryFn: () => api.space(slug).events.teams.mine(eventId),
    }),
  team: (slug: string, eventId: string, teamId: string) =>
    queryOptions({
      queryKey: ["t", slug, "events", eventId, "teams", teamId],
      queryFn: () => api.space(slug).events.teams.get(eventId, teamId),
    }),
  teamThread: (slug: string, eventId: string, teamId: string) =>
    queryOptions({
      queryKey: ["t", slug, "events", eventId, "teams", teamId, "comments"],
      queryFn: () => api.space(slug).events.teams.conversation(eventId, teamId).comments(),
    }),
};
